import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setLoader, setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { 
  getConnectionStatus, 
  quickAddBookingToCalendar,
  createCalendarEvent 
} from '../../utils/connectors/connectorsService';

/**
 * AddToCalendarButton Component
 * 
 * A reusable button that adds events to Google Calendar
 * Can be used with bookings, events, or custom data
 * 
 * Props:
 * - eventData: Object containing event details
 *   - summary (required): Event title
 *   - date (required): Event date
 *   - time (optional): Event time
 *   - location (optional): Event location
 *   - description (optional): Event description
 *   - endTime (optional): End time (defaults to 2 hours after start)
 * - buttonText: Text to display on button (default: "Add to Calendar")
 * - iconOnly: Show only icon (default: false)
 * - compact: Compact mode (default: false)
 * - onSuccess: Callback when event is added successfully
 */
const AddToCalendarButton = ({ 
  eventData, 
  buttonText = "Add to Calendar",
  iconOnly = false,
  compact = false,
  onSuccess 
}) => {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(true); // Start as true, disable if check fails
  const [isAdding, setIsAdding] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      setCheckingStatus(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const status = await getConnectionStatus('googleCalendar', token);
      setIsConnected(status.connected);
    } catch (error) {
      console.error('Error checking calendar connection:', error);
      setIsConnected(false);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleAddToCalendar = async (e) => {
    // Prevent event bubbling to parent elements
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!isConnected) {
      dispatch(setErrorAlert('Please connect Google first'));
      // Optionally redirect to connectors page
      setTimeout(() => {
        window.location.href = '/connectors/google';
      }, 2000);
      return;
    }

    if (!eventData?.summary || !eventData?.date) {
      dispatch(setErrorAlert('Missing required event data'));
      return;
    }

    try {
      setIsAdding(true);
      dispatch(setLoader(true));

      const token = JSON.parse(localStorage.getItem('dizeeToken'));

      // If it's a simple booking format (venue, date, time, location)
      if (eventData.venue && !eventData.description) {
        await quickAddBookingToCalendar({
          venue: eventData.venue || eventData.summary,
          date: eventData.date,
          time: eventData.time,
          location: eventData.location
        }, token);
      } 
      // Otherwise, use the full event creation API
      else {
        // Parse dates
        const startDate = new Date(eventData.date);
        if (eventData.time) {
          const [hours, minutes] = eventData.time.split(':');
          startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }

        // Calculate end time (default 2 hours later)
        let endDate;
        if (eventData.endTime) {
          endDate = new Date(eventData.date);
          const [hours, minutes] = eventData.endTime.split(':');
          endDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        } else {
          endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
        }

        await createCalendarEvent({
          summary: eventData.summary,
          description: eventData.description || '',
          location: eventData.location || '',
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          calendarId: eventData.calendarId || 'primary'
        }, token);
      }

      dispatch(setSuccessAlert('Event added to Google Calendar!'));
      
      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error adding to calendar:', error);
      dispatch(setErrorAlert('Failed to add event to calendar'));
    } finally {
      setIsAdding(false);
      dispatch(setLoader(false));
    }
  };


  // Icon only mode
  if (iconOnly) {
    return (
      <button
        onClick={handleAddToCalendar}
        disabled={isAdding || !isConnected}
        className={`
          flex items-center justify-center
          ${compact ? 'w-8 h-8' : 'w-10 h-10'}
          ${isConnected 
            ? 'text-[#4285F4] hover:text-[#1a73e8]' 
            : 'text-[rgba(255,255,255,0.3)]'
          }
          transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
        title={isConnected ? 'Add to Google Calendar' : 'Connect Google Calendar first'}
      >
        {isAdding ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  }

  // Compact mode
  if (compact) {
    return (
      <button
        onClick={handleAddToCalendar}
        disabled={isAdding || !isConnected}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-medium
          ${isConnected 
            ? 'bg-[rgba(66,133,244,0.1)] border border-[rgba(66,133,244,0.3)] text-[#4285F4] hover:bg-[rgba(66,133,244,0.2)]' 
            : 'bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white opacity-40'
          }
          transition-all
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {isAdding ? (
          <>
            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            <span>Adding...</span>
          </>
        ) : (
          <>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{buttonText}</span>
          </>
        )}
      </button>
    );
  }

  // Full button mode
  return (
    <button
      onClick={handleAddToCalendar}
      disabled={isAdding || !isConnected}
      className={`
        w-full flex items-center justify-center gap-2 p-4 rounded-lg text-[12px] font-medium
        ${isConnected 
          ? 'bg-[rgba(66,133,244,0.1)] border border-[rgba(66,133,244,0.3)] text-[#4285F4] hover:bg-[rgba(66,133,244,0.2)]' 
          : 'bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white opacity-40'
        }
        transition-all
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
    >
      {isAdding ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          <span>Adding to Calendar...</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>{isConnected ? buttonText : 'Connect Calendar First'}</span>
        </>
      )}
    </button>
  );
};

export default AddToCalendarButton;

